import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { get, post } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import SummaryCards from "components/SummaryCards";
import CustomPagination from "components/CustomPagination";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import secureLocalStorage from "react-secure-storage";
import { formattedDate } from "helpers/utils/dateUtils";

function Assignments() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [assignmentsData, setAssignmentsData] = useState([]);
  const [assignmentsCount, setAssignmentsCount] = useState({
    totalCount: 0,
    activeCount: 0,
    inActiveCount: 0,
    yetToStartCount: 0,
    onGoingCount: 0,
    completedCount: 0,
  });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [employeeList, setEmployeeList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "assignmentId",
    direction: "ASC",
  });
  const timerRef = useRef(null);
  const employeeId = secureLocalStorage.getItem("employeeId");

  const fetchAssignments = async (
    query,
    page = 1,
    sortBy = "assignmentId",
    direction = "ASC"
  ) => {
    try {
      setApiStatus({ loading: true, error: "" });

      const response = await post(api.GET_ALL_ASSIGNMENTS, {
        searchKey: query?.trim() ?? "",
        page: page - 1,
        pageSize: 10,
        employeeId: null,
        sortBy,
        direction,
      });

      setAssignmentsData(response?.paginationResponse?.data || []);
      setTotalPages(response?.paginationResponse?.totalPages || 0);
      setApiStatus({ loading: false, error: "" });
    } catch (error) {
      setApiStatus({
        loading: false,
        error: error.response?.data?.message || "Failed to fetch assignments",
      });
    }
  };

  const fetchCounts = async () => {
    try {
      setApiStatus({ loading: true, error: "" });
      const countData = await get(api.GET_ASSIGNMENTS_COUNT_DATA);
      setAssignmentsCount(countData?.data || {});
      setApiStatus({ loading: false, error: "" });
    } catch (error) {
      setApiStatus({
        loading: false,
        error: error.response?.data?.message || "Failed to fetch assignment counts",
      });
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const employees = res.map((item) => ({
        value: item.employeeId,
        label: `${item.firstName} ${item.lastName}`,
      }));
      setEmployeeList(employees);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  const handleSort = (columnName) => {
    const newDirection =
      sortConfig.sortBy === columnName && sortConfig.direction === "ASC"
        ? "DESC"
        : "ASC";

    setSortConfig({ sortBy: columnName, direction: newDirection });

    fetchAssignments(searchQuery, currentPage, columnName, newDirection);
  };

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  const columnSortMap = {
    "Assignment Code": "assignmentCode",
    Project: "projectName",
    "Assignment Title": "assignmentTitle",
    "Start Date": "startDate",
    "End Date": "endDate",
    "Reporting Manager": "reportingManagerId",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Assignment Code",
          "Project",
          "Assignment Title",
          "Start Date",
          "End Date",
          "Reporting Manager",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAssignments(searchQuery, page, sortConfig.sortBy, sortConfig.direction);
  };

  const handleSearchQuery = (e) => {
    const query = e.target.value ?? "";
    setSearchQuery(query);

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      fetchAssignments(query, 1, sortConfig.sortBy, sortConfig.direction);
    }, 300);
  };

  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case "YET_TO_START":
        return { label: "Yet to Start", color: "text-warning" };
      case "ONGOING":
        return { label: "On Going", color: "text-info" };
      case "COMPLETED":
        return { label: "Completed", color: "text-success" };
      default:
        return { label: "Unknown", color: "text-muted" };
    }
  };

  useEffect(() => {
    fetchCounts();
    fetchEmployees();
    fetchAssignments(searchQuery, currentPage, sortConfig.sortBy, sortConfig.direction);
  }, []);

  const summaryData = [
    {
      iconClass: "fas fa-list text-primary",
      label: "Total Count",
      value: assignmentsCount.totalCount,
    },
    {
      iconClass: "fas fa-hourglass-start text-warning",
      label: "Yet to Start",
      value: assignmentsCount.yetToStartCount,
    },
    {
      iconClass: "fas fa-sync-alt text-primary",
      label: "On Going",
      value: assignmentsCount.onGoingCount,
    },
    {
      iconClass: "fas fa-check-circle text-primary",
      label: "Completed",
      value: assignmentsCount.completedCount,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading="Assignments Summary"
          routePath="Home"
          pageName="Assignments"
        />
        <Row className="px-4">
          <SummaryCards data={summaryData} />

          <Row className="d-flex justify-content-between align-items-center py-2 mb-2">
            <Col xs={3}>
              <InputGroup size="sm" className="elbert-search-group">
                <Input
                  type="text"
                  placeholder="Search assignments"
                  value={searchQuery}
                  className="elbert-search"
                  onChange={handleSearchQuery}
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
            </Col>
            <Col xs={3}></Col>
            <Col xs={2} className="d-flex align-items-center justify-content-end">
              <CustomButton
                text="+ Add New"
                onClick={() => navigate("/add-assignment")}
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <colgroup>
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "9%" }} />
                      </colgroup>
                      {renderTableHeader()}
                      <tbody>
                        {assignmentsData.length > 0 ? (
                          assignmentsData.map((assignment, index) => {
                            const { label, color } = getStatusLabelAndColor(
                              assignment.status
                            );
                            return (
                              <tr key={index}>
                                <td>{assignment.assignmentCode}</td>
                                <td>{assignment.projectName}</td>
                                <td>{assignment.assignmentTitle}</td>
                                <td>{formattedDate(assignment.startDate)}</td>
                                <td>{formattedDate(assignment.endDate)}</td>
                                <td>
                                  {
                                    employeeList.find(
                                      (emp) =>
                                        emp.value ===
                                        assignment?.workLocationDTO?.reportingManagerId
                                    )?.label || "N/A"
                                  }
                                </td>
                                <td className={color}>{label}</td>
                                <td className="text-center">
                                  <div className="d-flex gap-3 align-items-center justify-content-center">
                                    <i
                                      onClick={() =>
                                        navigate(`/view-assignment/${assignment.assignmentId}`)
                                      }
                                      className="fa-solid fa-eye action-icon"
                                      title="View"
                                    ></i>
                                    <i
                                      onClick={() =>
                                        navigate(`/add-assignment/${assignment.assignmentId}`)
                                      }
                                      className="fa-regular fa-pen-to-square action-icon"
                                      title="Edit"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No Assignments data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    {apiStatus.error && (
                      <p className="text-danger text-center m-3">{apiStatus.error}</p>
                    )}
                    {apiStatus.loading && (
                      <p className="text-center m-3">Loading...</p>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Assignments;
